import React, { useState, useMemo, useEffect } from 'react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription
} from '@epoch-ui/components/Modal/modal';
import { RadioGroup, RadioGroupItem } from '@epoch-ui/components/RadioGroup/RadioGroup';
import { Separator } from '@epoch-ui/components/Separator/separator';
import { SearchInput } from '@epoch-ui/components/SearchInput/SearchInput';
import dayjs from 'utils/extendedDayJS';

const { timezoneNames } = dayjs;

type TimeZoneCode = string;

const NoResultsText = (): React.ReactElement => {
  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-py-5">
      <span className="tw-leading-5">We didn&apos;t find any results.</span>
      <span className="tw-leading-5">Try a different keyword.</span>
    </div>
  );
};

export interface TimeZoneSelectorModalProps {
  isOpen: boolean;
  timezoneCode: TimeZoneCode;
  onRequestClose: () => void;
  onChange: (timezone: TimeZoneCode) => void;
}

export const TimeZoneSelectorModal: React.FC<TimeZoneSelectorModalProps> = ({
  isOpen,
  onRequestClose,
  timezoneCode,
  onChange
}: TimeZoneSelectorModalProps): React.ReactElement => {
  const [searchText, setSearchText] = useState<string>('');
  const [filteredTimezones, setFilteredTimezones] = useState<string[]>(timezoneNames);
  const [newTimeZone, setNewTimeZone] = useState<TimeZoneCode>(timezoneCode);

  const modalDescription = useMemo(() => {
    return `You're scheduling the event in (${dayjs.getGMTOffset(
      timezoneCode
    )}) - ${dayjs.getTimezoneName(timezoneCode)}. `;
  }, [timezoneCode]);

  const filteredTimezonesData = useMemo(
    () =>
      searchText.length === 0
        ? [...new Set([timezoneCode, ...timezoneNames])]
        : timezoneNames.filter((tz: string) => tz.toLowerCase().includes(searchText.toLowerCase())),
    [searchText]
  );

  useEffect(() => {
    setFilteredTimezones(filteredTimezonesData);
  }, [filteredTimezonesData]);

  const handleTimeZoneChange = (tz: TimeZoneCode): void => {
    setNewTimeZone(tz);
    onChange(tz);
    onRequestClose();
  };

  return (
    <Modal open={isOpen} onOpenChange={onRequestClose}>
      <ModalContent
        size="md"
        className="tw-max-h-[80vh] tw-min-h-[3vh] tw-gap-y-2 tw-overflow-hidden tw-p-6"
      >
        <ModalHeader className="tw-gap-y-2">
          <ModalTitle>Select time zone</ModalTitle>
          <ModalDescription className="tw-text-14/20">{modalDescription}</ModalDescription>
        </ModalHeader>
        <SearchInput
          value={searchText}
          className="tw-w-full"
          onChange={(e): void => setSearchText(e.target.value)}
          debounce={400}
        />
        {filteredTimezones.length === 0 ? (
          <NoResultsText />
        ) : (
          <RadioGroup
            value={newTimeZone}
            onValueChange={(tz: TimeZoneCode): void => handleTimeZoneChange(tz)}
            className="tw-flex tw-flex-col"
          >
            <div className="tw-max-h-[676px] tw-max-w-[508px] tw-overflow-y-auto tw-overflow-x-hidden tw-scroll-smooth tw-pb-10">
              {filteredTimezones.map((tz: TimeZoneCode, index: number) => {
                const isLastItem = index === filteredTimezones.length - 1;
                return (
                  <div key={tz} className={`${isLastItem ? 'tw-pb-9' : ''}`}>
                    <RadioGroupItem
                      value={tz}
                      className="tw-flex tw-h-[52px] tw-flex-row tw-gap-x-5 tw-gap-y-4 tw-self-stretch tw-py-4 tw-leading-4"
                    >
                      <span>
                        {`(${dayjs.getGMTOffset(tz)}) ${tz.replace(/_/g, ' ')}`}{' '}
                        {dayjs.getTimezoneAbbr(tz)}
                      </span>
                    </RadioGroupItem>
                    {!isLastItem && <Separator orientation="horizontal" />}
                  </div>
                );
              })}
              <div className="tw-h-16" aria-hidden="true" />
            </div>
          </RadioGroup>
        )}
      </ModalContent>
    </Modal>
  );
};

TimeZoneSelectorModal.displayName = 'TimeZoneSelectorModal';
